import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import type { UserListItem } from "../../../types/userInvite";
import { UserListRow } from "./UserListRow";

interface UserListTableProps {
  isLoading: boolean;
  /*
   * PRO-709: false for a `settings` view-only user — the table stays readable
   * but loses the Actions column entirely (every action in it mutates or opens
   * an administrative RBAC surface). Defaults true.
   */
  canManageUsers?: boolean;
  users: UserListItem[];
  onDeactivate: (user: UserListItem) => void;
  onEdit: (user: UserListItem) => void;
  // PRO-413: RBAC v2 additive layer. When enabled, the Role column renders RBAC
  // role pills and rows gain a "Manage roles" action. Omitted/false = legacy view.
  rbacEnabled?: boolean;
  assignmentsByUserId?: Map<number, UserAssignment>;
  onManageRoles?: (user: UserListItem) => void;
  onViewAccess?: (user: UserListItem) => void;
}

const BASE_COLUMNS = ["Name", "Email", "Role", "Status", "Last activity"];
const ACTIONS_COLUMN = "Actions";

export const UserListTable = ({
  users,
  isLoading,
  onEdit,
  onDeactivate,
  canManageUsers = true,
  rbacEnabled = false,
  assignmentsByUserId,
  onManageRoles,
  onViewAccess,
}: UserListTableProps) => {
  const baseColumns = rbacEnabled
    ? ["Name", "Email", "Roles", "Status", "Last activity"]
    : BASE_COLUMNS;
  const columns = canManageUsers ? [...baseColumns, ACTIONS_COLUMN] : baseColumns;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {isLoading ? (
            <>
              {[0, 1, 2].map((row) => (
                <tr key={row} className="border-b border-gray-200">
                  {columns.map((col) => (
                    <td key={col} className="px-4 py-3">
                      <div className="h-4 rounded bg-gray-200 animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
            </>
          ) : users.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-sm text-gray-500"
              >
                {canManageUsers
                  ? "No users found. Invite someone to get started."
                  : "No users found."}
              </td>
            </tr>
          ) : (
            users.map((user) => (
              <UserListRow
                key={user.id}
                user={user}
                canManageUsers={canManageUsers}
                onEdit={onEdit}
                onDeactivate={onDeactivate}
                rbacEnabled={rbacEnabled}
                assignment={assignmentsByUserId?.get(user.id) ?? null}
                onManageRoles={onManageRoles}
                onViewAccess={onViewAccess}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
